import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Button, HStack, VStack, Input, Select, FormControl, FormLabel,
  Box, Text, Badge, Flex, Table, Thead, Tbody, Tr, Th, Td, IconButton, SimpleGrid,
  Divider, useToast, Tabs, TabList, TabPanels, Tab, TabPanel, Wrap, WrapItem,
  Menu, MenuButton, MenuList, MenuItem,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Radio, RadioGroup, Stack, Textarea,
} from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight, FiEdit2, FiSave, FiCopy, FiChevronDown, FiUser } from 'react-icons/fi';
import api from '../api';
import { useInvoiceStatuses } from '../hooks/useInvoiceStatuses';
import useMenuPosition from '../hooks/useMenuPosition';
import FileUpload from './FileUpload';
import PageShell from './PageShell';
import InvoiceModal from './InvoiceModal';
import AssignReceivedMoneyModal from './AssignReceivedMoneyModal';
import { ProcedureViewer } from './ProceduresDashboard';

const STATUS_LABELS = {
  // Shared
  quotation:'Quotation (Sent)', quotation_not_sent:'Quotation (Not Sent)', po_requested:'PO Requested', po_issued:'PO Issued',
  // OUT (client invoices)
  to_invoice:'To Invoice', created_not_sent:'Created (Not Sent)',
  sent_for_payment:'Sent for Payment', invoiced_within_terms:'Within Terms',
  paid_in_part:'Part Paid', paid_in_full:'Paid in Full',
  overdue:'Overdue', very_overdue:'Very Overdue',
  written_off:'Written Off', send_for_debt_collection:'Debt Collection',
  receipt:'Receipt', opportunity_value:'Opportunity Value',
  // IN (supplier invoices)
  recv_pay_within_terms:'Invoice Recv — PAY WITHIN TERMS',
  recv_do_not_pay:'Invoice Recv — Do NOT Pay Yet',
  recv_assign_po:'Invoice Recv — Assign PO',
  recv_refused:'Invoice Recv — REFUSED',
  recv_overdue:'Invoice Recv — OVERDUE',
  recv_receipt:'Receipt for Payment',
  recv_credited:'Credited by Supplier',
  // Shared — generic obsolete/credited (available to IN and OUT)
  obsolete:'Obsolete', credited:'Credited', superseded_quote:'Superseded Quote', deleted:'Deleted',
};
const STATUS_COLORS = {
  to_invoice:'yellow', created_not_sent:'gray', po_requested:'purple', po_issued:'purple',
  sent_for_payment:'blue', invoiced_within_terms:'blue', paid_in_part:'orange', paid_in_full:'green',
  overdue:'red', very_overdue:'red', written_off:'gray', send_for_debt_collection:'red',
  quotation:'purple', contracted:'cyan', pay_now:'red', projected:'pink', receipt:'green',
  opportunity_value:'orange',
  obsolete:'gray', credited:'teal', superseded_quote:'orange', deleted:'red',
};

export default function AccountingModal({ isOpen, onClose, fullPage, onBack, defaultDir, defaultStatusIn, defaultStatusOut }) {
  const active = fullPage || isOpen;
  // Raised-invoice statuses eligible for "Assign £ Received" — mirrors the
  // server-side filter on GET /invoices/assignable.
  const ASSIGNABLE_STATUSES = ['sent_for_payment', 'invoiced_within_terms', 'paid_in_part', 'overdue', 'very_overdue', 'send_for_debt_collection'];
  const [assignFocus, setAssignFocus] = useState(null); // invoice uuid to pre-expand in the assign modal
  // Accounts-category procedures for the selected invoice's project client
  // (migration 161) — surfaced as a non-blocking pop-out when paying or
  // raising invoices for a company that has a defined process. The fetch
  // effect lives below the `selected` declaration.
  const [acctProcs, setAcctProcs] = useState([]);
  const [procPopup, setProcPopup] = useState(null);
  const openProcPopup = async (procId) => {
    try { const { data } = await api.get(`/procedures/${procId}`); setProcPopup(data); } catch (e) {}
  };
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({ totalIn:0, totalOut:0, balance:0 });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterDir, setFilterDir] = useState(defaultDir || '');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterStatusIn, setFilterStatusIn] = useState(defaultStatusIn || '');
  const [filterStatusOut, setFilterStatusOut] = useState(defaultStatusOut || '');
  const { outStatuses, inStatuses, outMap, inMap } = useInvoiceStatuses();
  const [showProspects, setShowProspects] = useState(false);
  const [includeStartingBalance, setIncludeStartingBalance] = useState(false);
  const [boxPeriod, setBoxPeriod] = useState({
    raised: 'all', received: 'all', payable: 'all', paidOut: 'all'
  });
  const [ctxMenu, setCtxMenu] = useState(null); // { box, x, y }
  const ctxMenuRef = useRef(null);
  const ctxMenuPos = useMenuPosition(ctxMenuRef, ctxMenu);
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [selected, setSelected] = useState(null);
  // Fetch the client's Accounts procedures whenever the selected invoice
  // changes (project-based lookup; empty when nothing selected).
  useEffect(() => {
    let cancelled = false;
    const pid = selected?.project_id;
    if (!pid) { setAcctProcs([]); return; }
    api.get(`/procedures?project_id=${pid}&category=accounts`)
      .then(r => { if (!cancelled) setAcctProcs(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setAcctProcs([]); });
    return () => { cancelled = true; };
  }, [selected?.project_id]); // eslint-disable-line
  const [newInvoice, setNewInvoice] = useState(null); // { projectId, taskId, direction, launchAction } — Copy/New launcher
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, per_page: 50 });
      if (filterDir) params.set('direction', filterDir);
      // Use direction-specific status filter
      const effectiveStatus = filterDir === 'in' ? filterStatusIn : filterDir === 'out' ? filterStatusOut : filterStatus;
      if (effectiveStatus) params.set('status', effectiveStatus);
      if (filterFrom) params.set('date_from', filterFrom);
      if (filterTo) params.set('date_to', filterTo);
      const [invRes, sumRes] = await Promise.all([
        api.get(`/accounting/overview?${params}`),
        api.get(`/accounting/summary${filterFrom || filterTo ? `?date_from=${filterFrom}&date_to=${filterTo}` : ''}`),
      ]);
      setData(invRes.data.data || []); setTotal(invRes.data.total); setTotalPages(invRes.data.total_pages);
      setSummary(sumRes.data);
      if (sumRes.data.startingBalance !== undefined) {
        setStartingBalance(sumRes.data.startingBalance);
        setBalanceInput(String(sumRes.data.startingBalance));
      }
    } catch(e) {}
  }, [page, filterDir, filterStatus, filterStatusIn, filterStatusOut, filterFrom, filterTo]);

  useEffect(() => { if (active) { setPage(1); setSelected(null); } }, [active]);
  // Re-apply defaultDir whenever it changes (entry modal switches between raise/pay/ledger)
  useEffect(() => { setFilterDir(defaultDir || ''); setPage(1); }, [defaultDir]); // eslint-disable-line
  useEffect(() => { setFilterStatusIn(defaultStatusIn || ''); }, [defaultStatusIn]); // eslint-disable-line
  useEffect(() => { setFilterStatusOut(defaultStatusOut || ''); }, [defaultStatusOut]); // eslint-disable-line
  useEffect(() => { if (active) fetchData(); }, [active, fetchData]);

  const viewInvoice = async (id) => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setSelected(data);
      setItems(data.items || []);
      setEditing(false);
      setEditForm({
        external_invoice_id: data.external_invoice_id || '',
        purchase_order_number: data.purchase_order_number || '',
        status: data.status || '',
        payment_terms: data.payment_terms || '',
        date_paid: data.date_paid ? data.date_paid.split('T')[0] : '',
        payment_log_id: data.payment_log_id || '',
      });
    } catch(e) {}
  };

  const handleSaveEdit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/invoices/${selected.id}`, editForm);
      toast({ title: 'Invoice updated', status: 'success', duration: 1500 });
      await viewInvoice(selected.id);
      await fetchData();
      setEditing(false);
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  // ── Billing details editor (company name/address + invoice contact) ──
  const [billingEdit, setBillingEdit] = useState(null); // form object when open
  const [billingSaving, setBillingSaving] = useState(false);
  const [contactResults, setContactResults] = useState([]);

  const openBillingEdit = () => {
    const inv = selected || {};
    const isSupplierInv = inv.direction === 'in';
    setContactResults([]);
    setBillingEdit({
      scope: 'invoice',
      company_name: (isSupplierInv ? (inv.supplier_name || inv.company_name) : (inv.company_name || inv.supplier_name)) || '',
      company_address: (isSupplierInv ? (inv.supplier_address || inv.company_address) : (inv.company_address || inv.supplier_address)) || '',
      contact_id: inv.contact_id || '',
      contact_name: inv.contact_first_name ? `${inv.contact_first_name} ${inv.contact_last_name || ''}`.trim() : '',
      contact_email: inv.contact_email || '',
      contact_phone: inv.contact_phone || '',
      contact_mobile: inv.contact_mobile || '',
    });
  };
  const setB = (k, v) => setBillingEdit(p => ({ ...p, [k]: v }));

  const searchContacts = async (q) => {
    if (!q || q.length < 1) { setContactResults([]); return; }
    try { const { data } = await api.get(`/contacts/search?q=${encodeURIComponent(q)}&limit=8`); setContactResults(Array.isArray(data) ? data : []); }
    catch(e) { setContactResults([]); }
  };
  const pickContact = (c) => {
    setBillingEdit(p => ({
      ...p,
      contact_id: c.id,
      contact_name: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
      contact_email: c.email || '',
      contact_phone: c.phone || p.contact_phone || '',
      contact_mobile: c.mobile || '',
    }));
    setContactResults([]);
  };

  const saveBillingEdit = async () => {
    if (!selected || !billingEdit) return;
    setBillingSaving(true);
    try {
      await api.put(`/invoices/${selected.id}/billing`, {
        scope: billingEdit.scope,
        company_name: billingEdit.company_name,
        company_address: billingEdit.company_address,
        contact_id: billingEdit.contact_id || null,
        contact_name: billingEdit.contact_name,
        contact_email: billingEdit.contact_email,
        contact_phone: billingEdit.contact_phone,
        contact_mobile: billingEdit.contact_mobile,
      });
      toast({
        title: billingEdit.scope === 'main' ? 'Saved to the main records' : 'Saved to this invoice only',
        status: 'success', duration: 1800,
      });
      setBillingEdit(null);
      await viewInvoice(selected.id);
      await fetchData();
    } catch(e) { toast({ title: 'Could not save billing details', status: 'error', duration: 2500 }); }
    finally { setBillingSaving(false); }
  };

  const fmtDate = (d) => d ? new Date(d.split('T')[0] + 'T12:00:00').toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';
  const fmtMoney = (v) => `£${parseFloat(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // ── Invoice Detail View ──
  if (selected) {
    const inv = selected;
  const na = <Text fontSize="sm" color="gray.400" fontStyle="italic">Not added</Text>;
  const field = (label, value, color) => (
    <Box mb={3}>
      <Text fontSize="xs" color="gray.500" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" mb={1}>{label}</Text>
      {value ? <Text fontSize="md" fontWeight="500" color={color || 'gray.800'} whiteSpace="pre-wrap" lineHeight="1.45">{value}</Text> : na}
    </Box>
  );

  // Determine which company to show based on direction
  const isSupplierInv = inv.direction === 'in';
  const coName = isSupplierInv ? (inv.supplier_name || inv.company_name) : (inv.company_name || inv.supplier_name);
  const coAddr = isSupplierInv ? (inv.supplier_address || inv.company_address) : (inv.company_address || inv.supplier_address);
  const coPhone = isSupplierInv ? (inv.supplier_phone || inv.company_phone) : (inv.company_phone || inv.supplier_phone);
  const coNumber = isSupplierInv ? (inv.supplier_company_number || inv.company_number) : (inv.company_number || inv.supplier_company_number);
  const coVat = isSupplierInv ? (inv.supplier_vat_number || inv.company_vat_number) : (inv.company_vat_number || inv.supplier_vat_number);
  const coCode = isSupplierInv ? (inv.supplier_company_code || inv.company_code) : (inv.company_code || inv.supplier_company_code);
  const coWeb = isSupplierInv ? (inv.supplier_website || inv.company_website) : (inv.company_website || inv.supplier_website);

  const detailHeaderRight = (
    <HStack spacing={2}>
      {acctProcs.map(pp => (
        <Button key={pp.id} size="sm" colorScheme="orange" variant="outline"
          onClick={() => openProcPopup(pp.id)}>
          📋 {pp.name.length > 24 ? pp.name.slice(0, 24) + '…' : pp.name}
        </Button>
      ))}
      {inv.direction === 'out' && ASSIGNABLE_STATUSES.includes(inv.status) && (
        <Button size="sm" colorScheme="teal" variant="outline"
          onClick={() => setAssignFocus(inv.id)}>
          Assign Money Received
        </Button>
      )}
      <Badge colorScheme={STATUS_COLORS[inv.status]} fontSize="sm" px={3} py={1} borderRadius="md"
        bg={inv.status === 'to_invoice' ? 'yellow.300' : undefined}
        color={inv.status === 'to_invoice' ? 'gray.800' : undefined}>
        {STATUS_LABELS[inv.status] || inv.status}
      </Badge>
      {!!inv.is_overdue && inv.status === 'paid_in_part' && (
        <Badge colorScheme="red" fontSize="sm" px={3} py={1} borderRadius="md">OVERDUE</Badge>
      )}
      <Badge colorScheme={inv.direction === 'out' ? 'green' : 'red'} fontSize="sm" px={3} py={1} borderRadius="md">
        {inv.direction === 'out' ? '£ IN (Revenue)' : '£ OUT (Payment)'}
      </Badge>
    </HStack>
  );

    return (
      <PageShell fullPage={fullPage} onBack={() => setSelected(null)} isOpen={true} onClose={() => setSelected(null)} title="Invoice Detail" headerRight={detailHeaderRight}>
            <Box maxW="1150px" mx="auto" w="100%">
            <VStack spacing={6} align="stretch">
              {/* Top: Invoice ID + key dates / Status edit */}
              <Box bg={editing ? 'blue.50' : 'white'} p={6} borderRadius="xl" border="1px" borderColor={editing ? 'blue.200' : 'gray.200'} boxShadow="sm">
                {editing ? (
                  <VStack spacing={3}>
                    <HStack w="100%" spacing={3}>
                      <FormControl flex={1}><FormLabel fontSize="sm" mb={1}>Invoice ID</FormLabel>
                        <Input size="md" value={editForm.external_invoice_id} onChange={e => setEditForm(p => ({ ...p, external_invoice_id: e.target.value }))} placeholder="External invoice ref" /></FormControl>
                      <FormControl flex={1}><FormLabel fontSize="sm" mb={1}>PO Number</FormLabel>
                        <Input size="md" value={editForm.purchase_order_number} onChange={e => setEditForm(p => ({ ...p, purchase_order_number: e.target.value }))} /></FormControl>
                    </HStack>
                    <HStack w="100%" spacing={3}>
                      <FormControl flex={1}><FormLabel fontSize="sm" mb={1}>Status</FormLabel>
                        <Select size="md" value={editForm.status} onChange={e => setEditForm(p => ({ ...p, status: e.target.value }))}>
                          {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                        </Select></FormControl>
                      <FormControl flex={1}><FormLabel fontSize="sm" mb={1}>Payment Terms</FormLabel>
                        <Select size="md" value={editForm.payment_terms} onChange={e => setEditForm(p => ({ ...p, payment_terms: e.target.value }))}>
                          <option value="now_due">Now Due</option><option value="14_days">14 Days</option>
                          <option value="30_days">30 Days</option><option value="60_days">60 Days</option>
                        </Select></FormControl>
                    </HStack>
                    <HStack w="100%" spacing={3}>
                      <FormControl flex={1}><FormLabel fontSize="sm" mb={1}>Date Paid</FormLabel>
                        <Input size="md" type="date" value={editForm.date_paid} onChange={e => setEditForm(p => ({ ...p, date_paid: e.target.value }))} /></FormControl>
                      <FormControl flex={1}><FormLabel fontSize="sm" mb={1}>Payment Log ID</FormLabel>
                        <Input size="md" value={editForm.payment_log_id} onChange={e => setEditForm(p => ({ ...p, payment_log_id: e.target.value }))} placeholder="Manual log ref" /></FormControl>
                    </HStack>
                    <HStack spacing={2} alignSelf="flex-end">
                      <Button size="md" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
                      <Button size="md" colorScheme="blue" leftIcon={<FiSave />} onClick={handleSaveEdit} isLoading={saving}>Save</Button>
                    </HStack>
                  </VStack>
                ) : (
                  <Flex justify="space-between" align="start" gap={4}>
                    <Box flex={1}>
                      <Text fontSize="2xl" fontWeight="800" color="gray.800" lineHeight="1.1" mb={4}>
                        {inv.invoice_number || inv.invoice_ref || 'Invoice'}
                      </Text>
                      <SimpleGrid columns={{ base: 2, md: 4 }} spacingX={8} spacingY={3}>
                        {field('External Invoice ID', inv.external_invoice_id)}
                        {field('Purchase Order (PO)', inv.purchase_order_number)}
                        {field('Issue Date', fmtDate(inv.issue_date))}
                        {field('Due Date', fmtDate(inv.due_date))}
                        {field('Payment Terms', (inv.payment_terms || '').replace(/_/g, ' '))}
                        {field('Date Paid', inv.date_paid ? fmtDate(inv.date_paid) : null, 'green.600')}
                        {field('Payment Log ID', inv.payment_log_id)}
                      </SimpleGrid>
                    </Box>
                    <Button leftIcon={<FiEdit2 />} size="md" colorScheme="blue" variant="outline" onClick={() => setEditing(true)} flexShrink={0}>Edit</Button>
                  </Flex>
                )}
              </Box>

              {/* Company / Contact / Project — three professional cards */}
              <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={5}>
                <Box border="1px" borderColor="gray.200" borderRadius="xl" p={5} bg="white" boxShadow="sm">
                  <Flex justify="space-between" align="center" mb={4}>
                    <Badge bg="gray.700" color="white" fontSize="xs" px={3} py={1.5} borderRadius="md" letterSpacing="0.05em">
                      {isSupplierInv ? 'SUPPLIER' : 'CLIENT'} COMPANY
                    </Badge>
                    <Button size="xs" variant="ghost" colorScheme="blue" leftIcon={<FiEdit2 size={12} />} onClick={openBillingEdit}>Edit</Button>
                  </Flex>
                  {field('Company Name', coName)}
                  {field('Address', coAddr)}
                  {field('Phone', coPhone)}
                  {field('Company Number', coNumber)}
                  {field('VAT Number', coVat)}
                  {field('Company ID (Sage)', coCode)}
                  {field('Website', coWeb)}
                </Box>

                <Box border="1px" borderColor="gray.200" borderRadius="xl" p={5} bg="white" boxShadow="sm">
                  <Flex justify="space-between" align="center" mb={4}>
                    <Badge bg="blue.600" color="white" fontSize="xs" px={3} py={1.5} borderRadius="md" letterSpacing="0.05em">
                      INVOICE CONTACT
                    </Badge>
                    <Button size="xs" variant="ghost" colorScheme="blue" leftIcon={<FiUser size={12} />} onClick={openBillingEdit}>
                      {selected.contact_first_name || selected.bill_contact_name ? 'Edit' : 'Assign'}
                    </Button>
                  </Flex>
                  {field('Name', inv.contact_first_name ? `${inv.contact_first_name} ${inv.contact_last_name || ''}` : null)}
                  {field('Email', inv.contact_email)}
                  {field('Phone', inv.contact_phone)}
                  {field('Mobile', inv.contact_mobile)}
                </Box>

                <Box border="1px" borderColor="gray.200" borderRadius="xl" p={5} bg="white" boxShadow="sm">
                  <Badge bg="teal.600" color="white" fontSize="xs" px={3} py={1.5} borderRadius="md" mb={4} letterSpacing="0.05em">
                    PROJECT / TASK
                  </Badge>
                  {field('Project', inv.project_name)}
                  {field('Task', inv.task_title)}
                  {field('Send To', inv.contact_email || (coAddr ? `${coName}, ${coAddr}` : null))}
                  {inv.creation_reason && field('Invoice Notes', inv.creation_reason)}
                </Box>
              </SimpleGrid>

              {/* Invoicing Workflow Checklist */}
              {(() => {
                const wfItems = [];
                const poRequired = inv.task_po_required || inv.project_po_required || inv.company_po_required;
                if (poRequired) {
                  const poNum = inv.purchase_order_number;
                  wfItems.push({ key:'po', done: !!poNum, label: 'Purchase Order Required', detail: poNum ? `PO: ${poNum}` : 'No PO number recorded yet', color: poNum ? 'green' : 'orange' });
                }
                if (inv.task_invoicing_notes) wfItems.push({ key:'task_notes', done: false, label: 'Task Notes', detail: inv.task_invoicing_notes, color: 'blue', infoOnly: true });
                if (inv.project_invoicing_notes) wfItems.push({ key:'proj_notes', done: false, label: 'Project Notes', detail: inv.project_invoicing_notes, color: 'blue', infoOnly: true });
                if (inv.company_invoicing_notes) wfItems.push({ key:'co_notes', done: false, label: 'Company Invoicing Notes', detail: inv.company_invoicing_notes, color: 'blue', infoOnly: true });
                if (wfItems.length === 0) return null;
                return (
                  <Box>
                    <Text fontSize="sm" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.06em" mb={2}>Invoicing Workflow</Text>
                    <VStack spacing={2} align="stretch">
                      {wfItems.map(it => (
                        <Flex key={it.key} align="flex-start" gap={3} bg={`${it.color}.50`} borderRadius="lg" px={4} py={3} border="1px" borderColor={`${it.color}.200`}>
                          <Box mt="2px" w="20px" h="20px" borderRadius="full" flexShrink={0}
                            bg={it.infoOnly ? 'blue.400' : it.done ? 'green.400' : 'orange.400'}
                            display="flex" alignItems="center" justifyContent="center">
                            <Text fontSize="xs" color="white" fontWeight="800">{it.infoOnly ? 'i' : it.done ? '✓' : '!'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="md" fontWeight="700" color={`${it.color}.700`}>{it.label}</Text>
                            <Text fontSize="sm" color="gray.600">{it.detail}</Text>
                          </Box>
                        </Flex>
                      ))}
                    </VStack>
                  </Box>
                );
              })()}

              {/* Line Items */}
              <Box border="1px" borderColor="gray.200" borderRadius="xl" overflow="hidden" boxShadow="sm">
                <Text fontSize="sm" fontWeight="700" color="gray.600" textTransform="uppercase" letterSpacing="0.06em" px={5} pt={4} pb={2}>Line Items</Text>
                {items.length > 0 ? (
                  <Table size="md">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th py={3} fontSize="xs">Code</Th><Th py={3} fontSize="xs">Nominal</Th><Th py={3} fontSize="xs">Description</Th><Th py={3} fontSize="xs" isNumeric>Qty</Th>
                        <Th py={3} fontSize="xs" isNumeric>Unit Price</Th><Th py={3} fontSize="xs" isNumeric>VAT %</Th>
                        <Th py={3} fontSize="xs" isNumeric>Line VAT</Th><Th py={3} fontSize="xs" isNumeric>Gross</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {items.map(item => {
                        const lineNet = (parseFloat(item.quantity) || 1) * (parseFloat(item.net_price) || 0);
                        const lineVat = lineNet * ((parseFloat(item.vat_rate) || 0) / 100);
                        const hasDiscount = item.custom_description && item.custom_description !== item.description;
                        if (item.is_note) return (
                          // Sage convention: "M" is the product code for a
                          // message line — an ordinary row with the text in
                          // Description and the value columns left blank.
                          <Tr key={item.id}>
                            <Td py={3} fontSize="sm" fontWeight="600">M</Td>
                            <Td py={3} fontSize="sm"></Td>
                            <Td py={3} fontSize="sm" maxW="420px">{item.description || ''}</Td>
                            <Td py={3}></Td><Td py={3}></Td><Td py={3}></Td><Td py={3}></Td><Td py={3}></Td>
                          </Tr>
                        );
                        return (
                          <Tr key={item.id} bg={hasDiscount ? 'yellow.50' : undefined}>
                            <Td py={3} fontSize="sm" fontWeight="600">{item.item_code || '—'}</Td>
                            <Td py={3} fontSize="sm" color="gray.600">{item.nominal_code || '—'}</Td>
                            <Td py={3} fontSize="sm" maxW="420px">
                              <Text>{item.description || '—'}</Text>
                              {item.custom_description && item.custom_description !== item.description && (
                                <Text fontSize="xs" color="orange.600" fontStyle="italic" bg="yellow.100" px={1.5} py={0.5} borderRadius="sm" mt={1} display="inline-block">
                                  Modified: {item.custom_description}
                                </Text>
                              )}
                            </Td>
                            <Td py={3} fontSize="sm" isNumeric>{item.quantity ?? "—"}</Td>
                            <Td py={3} fontSize="sm" isNumeric>{fmtMoney(item.net_price)}</Td>
                            <Td py={3} fontSize="sm" isNumeric>{item.vat_rate != null ? `${item.vat_rate}%` : "—"}</Td>
                            <Td py={3} fontSize="sm" isNumeric>{fmtMoney(lineVat)}</Td>
                            <Td py={3} fontSize="sm" isNumeric fontWeight="700">{fmtMoney(lineNet + lineVat)}</Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                ) : <Text fontSize="md" color="gray.400" px={5} pb={4}>No line items</Text>}
              </Box>

              {/* Totals */}
              <Flex justify="flex-end">
                <Box minW="280px" bg="gray.50" border="1px" borderColor="gray.200" p={5} borderRadius="xl">
                  <Flex justify="space-between" mb={2}><Text fontSize="md" color="gray.600">Net</Text><Text fontSize="md" fontWeight="600">{fmtMoney(inv.total_net)}</Text></Flex>
                  <Flex justify="space-between" mb={2}><Text fontSize="md" color="gray.600">VAT</Text><Text fontSize="md" fontWeight="600">{fmtMoney(inv.total_vat)}</Text></Flex>
                  <Divider my={2} />
                  <Flex justify="space-between" align="center"><Text fontSize="xl" fontWeight="800" color="gray.800">Gross</Text><Text fontSize="xl" fontWeight="800" color="brand.600">{fmtMoney(inv.total_gross)}</Text></Flex>
                  {parseFloat(inv.amount_paid) > 0 && (
                    <Flex justify="space-between" mt={3} pt={2} borderTop="1px" borderColor="gray.200">
                      <Text fontSize="md" color="green.600" fontWeight="600">Paid</Text>
                      <Text fontSize="md" color="green.600" fontWeight="700">{fmtMoney(inv.amount_paid)}</Text>
                    </Flex>
                  )}
                </Box>
              </Flex>

              {/* File Uploads */}
              <Box pt={2}>
                <Text fontSize="sm" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.06em" mb={3}>Attachments (Invoice PDF / Contract)</Text>
                <FileUpload entityType="invoice" entityId={inv.id} folder="invoices" accept=".pdf,image/*" label="Upload PDF or image" />
              </Box>

              {/* Billing details editor */}
              {billingEdit && (
                <Modal isOpen={true} onClose={() => setBillingEdit(null)} size="xl" isCentered>
                  <ModalOverlay />
                  <ModalContent>
                    <ModalHeader fontSize="lg">Edit Invoice Billing Details</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                      <VStack spacing={4} align="stretch">
                        <FormControl>
                          <FormLabel fontSize="sm">Company Name</FormLabel>
                          <Input value={billingEdit.company_name} onChange={e => setB('company_name', e.target.value)} />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="sm">Company Address</FormLabel>
                          <Textarea value={billingEdit.company_address} onChange={e => setB('company_address', e.target.value)} rows={3} />
                        </FormControl>

                        <Divider />
                        <Text fontSize="sm" fontWeight="700" color="gray.600">Invoice Contact</Text>
                        <FormControl>
                          <FormLabel fontSize="xs" color="gray.500">Find an existing contact (optional)</FormLabel>
                          <Input placeholder="Type a name or email…" onChange={e => searchContacts(e.target.value)} />
                          {contactResults.length > 0 && (
                            <Box mt={1} border="1px" borderColor="gray.200" borderRadius="md" maxH="160px" overflowY="auto">
                              {contactResults.map(c => (
                                <Box key={c.id} px={3} py={2} cursor="pointer" _hover={{ bg: 'blue.50' }} onClick={() => pickContact(c)}>
                                  <Text fontSize="sm" fontWeight="600">{`${c.first_name || ''} ${c.last_name || ''}`.trim() || '—'}</Text>
                                  <Text fontSize="xs" color="gray.500">{[c.email, c.company_name].filter(Boolean).join(' · ')}</Text>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </FormControl>
                        <SimpleGrid columns={2} spacing={3}>
                          <FormControl><FormLabel fontSize="xs" color="gray.500">Name</FormLabel><Input value={billingEdit.contact_name} onChange={e => setB('contact_name', e.target.value)} /></FormControl>
                          <FormControl><FormLabel fontSize="xs" color="gray.500">Email</FormLabel><Input value={billingEdit.contact_email} onChange={e => setB('contact_email', e.target.value)} /></FormControl>
                          <FormControl><FormLabel fontSize="xs" color="gray.500">Phone</FormLabel><Input value={billingEdit.contact_phone} onChange={e => setB('contact_phone', e.target.value)} /></FormControl>
                          <FormControl><FormLabel fontSize="xs" color="gray.500">Mobile</FormLabel><Input value={billingEdit.contact_mobile} onChange={e => setB('contact_mobile', e.target.value)} /></FormControl>
                        </SimpleGrid>

                        <Divider />
                        <RadioGroup value={billingEdit.scope} onChange={v => setB('scope', v)}>
                          <Stack spacing={3}>
                            <Radio value="invoice" colorScheme="blue">
                              <Box>
                                <Text fontSize="sm" fontWeight="600">Apply changes to this invoice only</Text>
                                <Text fontSize="xs" color="gray.500">Saves these details on this invoice. The company/contact records and all other invoices are untouched.</Text>
                              </Box>
                            </Radio>
                            <Radio value="main" colorScheme="blue">
                              <Box>
                                <Text fontSize="sm" fontWeight="600">Apply changes to the main records</Text>
                                <Text fontSize="xs" color="gray.500">Updates the company/contact master records. Previous invoices keep their original details.</Text>
                              </Box>
                            </Radio>
                          </Stack>
                        </RadioGroup>
                      </VStack>
                    </ModalBody>
                    <ModalFooter gap={2}>
                      <Button variant="ghost" onClick={() => setBillingEdit(null)}>Cancel</Button>
                      <Button colorScheme="blue" leftIcon={<FiSave />} onClick={saveBillingEdit} isLoading={billingSaving}>Save</Button>
                    </ModalFooter>
                  </ModalContent>
                </Modal>
              )}
            </VStack>
            </Box>
            {/* Assign money received — launched from the header button; on
                close, refresh both the detail (status may now be Part Paid /
                Paid in Full) and the ledger list behind it. */}
            <AssignReceivedMoneyModal
              isOpen={!!assignFocus}
              focusInvoiceId={assignFocus}
              onClose={() => { setAssignFocus(null); viewInvoice(inv.id); fetchData(); }}
              onAssigned={() => { viewInvoice(inv.id); fetchData(); }}
            />
            {/* Accounts-procedure pop-up viewer — non-blocking layer */}
            {procPopup && (
              <Modal isOpen={true} onClose={() => setProcPopup(null)} size="3xl" scrollBehavior="inside">
                <ModalOverlay bg="blackAlpha.300" />
                <ModalContent maxH="85vh">
                  <ModalHeader fontSize="md">📋 {procPopup.name}
                    {procPopup.category_name && <Badge ml={2} colorScheme="teal" fontSize="9px">{procPopup.category_name}</Badge>}
                  </ModalHeader>
                  <ModalCloseButton />
                  <ModalBody overflowY="auto" onWheel={e => e.stopPropagation()}><ProcedureViewer procedure={procPopup} /></ModalBody>
                  <ModalFooter><Button size="sm" onClick={() => setProcPopup(null)}>Close</Button></ModalFooter>
                </ModalContent>
              </Modal>
            )}
      </PageShell>
    );
  }

  // ── List View ──
  const modeBanner = defaultDir === 'out' ? (
    <Box bg="green.500" color="white" px={4} py={2} borderRadius="md" mb={3}
      display="flex" alignItems="center" gap={2}>
      <Text fontSize="18px">📤</Text>
      <Box>
        <Text fontSize="13px" fontWeight="800">Raise Invoices — Money IN</Text>
        <Text fontSize="10px" opacity={0.85}>Showing invoices sent to clients. Filter or search below.</Text>
      </Box>
    </Box>
  ) : defaultDir === 'in' ? (
    <Box bg="red.500" color="white" px={4} py={2} borderRadius="md" mb={3}
      display="flex" alignItems="center" gap={2}>
      <Text fontSize="18px">📥</Text>
      <Box>
        <Text fontSize="13px" fontWeight="800">Pay Invoices — Money OUT</Text>
        <Text fontSize="10px" opacity={0.85}>Showing supplier invoices received. Filter or search below.</Text>
      </Box>
    </Box>
  ) : null;

  const listContent = (
    <>
      {modeBanner}
      {/* ── Stats Boxes ── */}
      {(() => {
        // Helper: get period data bucket
        const P = (box) => {
          const key = boxPeriod[box] || 'all';
          return (summary.periods && summary.periods[key]) ? summary.periods[key] : summary;
        };
        const pd = { raised: P('raised'), received: P('received'), payable: P('payable'), paidOut: P('paidOut') };

        const sb = includeStartingBalance ? (summary.startingBalance || 0) : 0;
        // When starting balance included, don't re-add historical received/paid (they're in the balance already)
        const receivedForBalance = includeStartingBalance ? 0 : (pd.received.paidIn || 0);
        const paidForBalance     = includeStartingBalance ? 0 : (pd.paidOut.receiptsPaid || 0);
        const raisedForBalance   = pd.raised.raisedSentUnpaid || 0;
        const payableForBalance  = (pd.payable.toPayOut || 0) - (pd.payable.receiptsPaid || 0);

        const balanceCalc        = sb + receivedForBalance - paidForBalance;
        const balanceDue         = balanceCalc + raisedForBalance - payableForBalance;
        const balancePipeline    = balanceDue + (pd.raised.prospectsValue || 0);

        const PERIOD_OPTS_FUTURE = [
          { key: 'all',       label: 'All time' },
          { key: 'thisMonth', label: 'This month' },
          { key: 'next30',    label: 'Next 30 days' },
          { key: 'next60',    label: 'Next 60 days' },
        ];
        const PERIOD_OPTS_PAST = [
          { key: 'all',       label: 'All time' },
          { key: 'thisMonth', label: 'This month' },
          { key: 'last30',    label: 'Last 30 days' },
          { key: 'last60',    label: 'Last 60 days' },
        ];
        const PERIOD_OPTS_BALANCE = [
          { key: 'useStarting', label: 'Include starting balance' },
        ];

        const periodLabel = (box, opts) => {
          const k = boxPeriod[box] || 'all';
          return opts.find(o => o.key === k)?.label || 'All time';
        };

        const StatBox = ({ id, bg, borderC, titleColor, subtitleColor, children, periodOpts, extraOpts }) => (
          <Box bg={bg} p={4} borderRadius="lg" border="1px" borderColor={borderC}
            position="relative" userSelect="none" minW="148px"
            onContextMenu={e => { e.preventDefault(); setCtxMenu({ box: id, x: e.clientX, y: e.clientY, opts: periodOpts, extraOpts }); }}>
            <Box fontSize="9px" color={subtitleColor || 'gray.400'} position="absolute" top="5px" right="8px"
              fontStyle="italic" cursor="context-menu" title="Right-click for period options">
              {id === 'balance' ? (includeStartingBalance ? '+ Starting Bal' : '') : periodLabel(id, periodOpts)}
            </Box>
            {children}
          </Box>
        );

        return (
          <>
          <Flex gap={2} mb={2} flexWrap="wrap" align="stretch">

            {/* Box 1: Starting Balance */}
            <Box flex="0 0 auto" minW="148px" bg="gray.50" p={4} borderRadius="lg" border="1px" borderColor="gray.200" textAlign="center">
              <Text fontSize="18px" fontWeight="800" color="gray.700">{fmtMoney(summary.startingBalance || 0)}</Text>
              <Text fontSize="11px" color="gray.500" fontWeight="700" mt={1}>Starting Balance</Text>
              <Text fontSize="10px" color="gray.400">Main bank account</Text>
              <Button size="xs" mt={1}
                variant={includeStartingBalance ? 'solid' : 'outline'}
                colorScheme={includeStartingBalance ? 'teal' : 'gray'}
                fontSize="8px" h="18px" px={2}
                onClick={() => setIncludeStartingBalance(!includeStartingBalance)}>
                {includeStartingBalance ? '✓ Included' : 'Include'}
              </Button>
            </Box>

            {/* Box 2: Invoiced & Due */}
            <StatBox id="raised" bg="blue.50" borderC="blue.100" titleColor="blue.700" subtitleColor="blue.400"
              periodOpts={PERIOD_OPTS_FUTURE}>
              <Text fontSize="18px" fontWeight="800" color="blue.700">{fmtMoney(pd.raised.raisedSent || 0)}</Text>
              <Text fontSize="11px" color="blue.500" fontWeight="700" mt={1}>Invoiced & Due</Text>
              <Text fontSize="10px" color="gray.400">Sent, awaiting payment</Text>
              {(pd.raised.raisedSentUnpaid || 0) !== (pd.raised.raisedSent || 0) && (
                <Text fontSize="8px" color="blue.400" mt={0.5}>Unpaid: {fmtMoney(pd.raised.raisedSentUnpaid || 0)}</Text>
              )}
            </StatBox>

            {/* Box 3: Payments Received */}
            <StatBox id="received" bg="green.50" borderC="green.100" titleColor="green.700" subtitleColor="green.400"
              periodOpts={PERIOD_OPTS_PAST}>
              <Text fontSize="18px" fontWeight="800" color="green.700">{fmtMoney(pd.received.paidIn || 0)}</Text>
              <Text fontSize="11px" color="green.600" fontWeight="700" mt={1}>Received £ IN</Text>
              <Text fontSize="10px" color="gray.400">Cash received</Text>
            </StatBox>

            {/* Box 4: Payable OUT */}
            <StatBox id="payable" bg="orange.50" borderC="orange.100" titleColor="orange.700" subtitleColor="orange.400"
              periodOpts={PERIOD_OPTS_FUTURE}>
              <Text fontSize="18px" fontWeight="800" color="orange.700">{fmtMoney(pd.payable.toPayOut || 0)}</Text>
              <Text fontSize="11px" color="orange.600" fontWeight="700" mt={1}>Payable OUT</Text>
              <Text fontSize="10px" color="gray.400">Supplier invoices due</Text>
              <Text fontSize="8px" color="orange.400" mt={0.5}>Unpaid: {fmtMoney((pd.payable.toPayOut||0)-(pd.payable.receiptsPaid||0))}</Text>
            </StatBox>

            {/* Box 5: Paid Out */}
            <StatBox id="paidOut" bg="red.50" borderC="red.100" titleColor="red.700" subtitleColor="red.400"
              periodOpts={PERIOD_OPTS_PAST}>
              <Text fontSize="18px" fontWeight="800" color="red.700">{fmtMoney(pd.paidOut.receiptsPaid || 0)}</Text>
              <Text fontSize="11px" color="red.600" fontWeight="700" mt={1}>Paid Out</Text>
              <Text fontSize="10px" color="gray.400">Cash paid to suppliers</Text>
            </StatBox>

            {/* Prospects box — only when enabled */}
            {showProspects && (
              <Box flex="0 0 auto" minW="120px" bg="orange.50" p={3} borderRadius="md"
                border="2px" borderColor="#EE7203" textAlign="center">
                <Text fontSize="18px" fontWeight="800" color="#EE7203">{fmtMoney(pd.raised.prospectsValue || 0)}</Text>
                <Text fontSize="11px" fontWeight="700" color="#EE7203" mt={1}>Prospects</Text>
                <Text fontSize="10px" color="gray.400">Opportunity value</Text>
              </Box>
            )}

            {/* Box 6: BALANCE — rightmost, wider */}
            <StatBox id="balance" bg={balanceCalc >= 0 ? 'teal.50' : 'red.50'}
              borderC={balanceCalc >= 0 ? 'teal.200' : 'red.200'}
              periodOpts={[]} extraOpts={PERIOD_OPTS_BALANCE}>
              <Text fontSize="20px" fontWeight="800" color={balanceCalc >= 0 ? 'teal.700' : 'red.700'}>
                {fmtMoney(balanceCalc)}</Text>
              <Text fontSize="11px" fontWeight="700" color={balanceCalc >= 0 ? 'teal.600' : 'red.600'} mt={1}>Balance</Text>
              <Text fontSize="8px" color="gray.500">
                {includeStartingBalance ? 'Starting + Received − Paid' : 'Received − Paid out'}
              </Text>
              <Box mt={1} pt={1} borderTop="1px" borderColor={balanceCalc >= 0 ? 'teal.100' : 'red.100'}>
                <Text fontSize="8px" color="gray.600">
                  <Text as="span" fontWeight="700" color="blue.600">Due: </Text>{fmtMoney(balanceDue)}
                  <Text as="span" color="gray.400"> (−payable)</Text>
                </Text>
                {showProspects && (
                  <Text fontSize="8px" color="gray.600" mt={0.5}>
                    <Text as="span" fontWeight="700" color="#EE7203">Pipeline: </Text>{fmtMoney(balancePipeline)}
                  </Text>
                )}
              </Box>
            </StatBox>

          </Flex>

          {/* Right-click context menu */}
          {ctxMenu && (
            <>
              <Box position="fixed" inset={0} zIndex={998} onClick={() => setCtxMenu(null)} />
              <Box ref={ctxMenuRef}
                position="fixed" top={ctxMenuPos.top} left={ctxMenuPos.left} zIndex={999}
                bg="white" border="1px" borderColor="gray.200" borderRadius="md" shadow="lg" py={1} minW="180px"
                maxH={ctxMenuPos.maxHeight} maxW={ctxMenuPos.maxWidth} overflowY="auto" overflowX="hidden"
                visibility={ctxMenuPos.ready ? 'visible' : 'hidden'}>
                <Text fontSize="9px" fontWeight="700" color="gray.400" px={3} pt={1} pb={0.5} textTransform="uppercase">Period</Text>
                {(ctxMenu.opts || []).map(o => (
                  <Box key={o.key} px={3} py={1.5} cursor="pointer" fontSize="12px"
                    bg={(boxPeriod[ctxMenu.box] || 'all') === o.key ? 'teal.50' : 'white'}
                    color={(boxPeriod[ctxMenu.box] || 'all') === o.key ? 'teal.700' : 'gray.700'}
                    fontWeight={(boxPeriod[ctxMenu.box] || 'all') === o.key ? '700' : '400'}
                    _hover={{ bg: 'gray.50' }}
                    onClick={() => { setBoxPeriod(prev => ({ ...prev, [ctxMenu.box]: o.key })); setCtxMenu(null); }}>
                    {(boxPeriod[ctxMenu.box] || 'all') === o.key ? '✓ ' : ''}{o.label}
                  </Box>
                ))}
                {ctxMenu.extraOpts && ctxMenu.extraOpts.length > 0 && (
                  <>
                    <Box h="1px" bg="gray.100" my={1} />
                    {ctxMenu.extraOpts.map(o => (
                      <Box key={o.key} px={3} py={1.5} cursor="pointer" fontSize="12px"
                        bg={o.key === 'useStarting' && includeStartingBalance ? 'teal.50' : 'white'}
                        color={o.key === 'useStarting' && includeStartingBalance ? 'teal.700' : 'gray.700'}
                        fontWeight={o.key === 'useStarting' && includeStartingBalance ? '700' : '400'}
                        _hover={{ bg: 'gray.50' }}
                        onClick={() => {
                          if (o.key === 'useStarting') setIncludeStartingBalance(!includeStartingBalance);
                          setCtxMenu(null);
                        }}>
                        {o.key === 'useStarting' && includeStartingBalance ? '✓ ' : ''}{o.label}
                      </Box>
                    ))}
                  </>
                )}
              </Box>
            </>
          )}
          </>
        );
      })()}

      <Box bg="gray.50" p={3} borderRadius="md" mb={3}>
        {/* Show Prospects toggle */}
        <Flex justify="space-between" align="center" mb={2}>
          <Button size="xs"
            variant={showProspects ? 'solid' : 'outline'}
            bg={showProspects ? '#EE7203' : 'transparent'}
            borderColor={showProspects ? '#EE7203' : 'gray.300'}
            color={showProspects ? 'white' : 'gray.500'}
            _hover={{ bg: showProspects ? '#c05600' : 'gray.50' }}
            onClick={() => setShowProspects(!showProspects)}
            leftIcon={<Text fontSize="10px">📊</Text>}
          >
            {showProspects ? 'Hiding Opportunities' : 'Show Value of Opportunities (Prospects)'}
          </Button>
        </Flex>

        {/* Direction tabs */}
        <Tabs index={filterDir === '' ? 0 : filterDir === 'out' ? 1 : 2} size="sm" mb={3}
          onChange={i => { setFilterDir(i===0?'':i===1?'out':'in'); setPage(1); }}>
          <TabList>
            <Tab fontSize="11px">All</Tab>
            <Tab fontSize="11px" color="green.600" fontWeight="700">£ IN — Revenue</Tab>
            <Tab fontSize="11px" color="red.600" fontWeight="700">£ OUT — Payments</Tab>
          </TabList>
          <TabPanels>
            {/* All — combined status filter */}
            <TabPanel px={0} pb={1} pt={2}>
              <HStack spacing={2} flexWrap="wrap">
                <FormControl w="180px"><FormLabel fontSize="xs" mb={0.5}>Status</FormLabel>
                  <Select size="sm" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
                    <option value="">All Statuses</option>
                    {[...outStatuses.filter(s=>s.enabled), ...inStatuses.filter(s=>s.enabled&&!outStatuses.find(o=>o.key===s.key&&o.enabled))].map(s => (
                      <option key={s.key} value={s.key}>{s.label}</option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>
            </TabPanel>
            {/* £ IN status filter */}
            <TabPanel px={0} pb={1} pt={2}>
              <Text fontSize="10px" color="gray.500" mb={1}>Filter by status:</Text>
              <Wrap spacing={1}>
                <WrapItem>
                  <Button size="xs" variant={filterStatusOut===''?'solid':'outline'} colorScheme={filterStatusOut===''?'green':'gray'}
                    onClick={() => { setFilterStatusOut(''); setPage(1); }}>All £ IN</Button>
                </WrapItem>
                {outStatuses.filter(s=>s.enabled).map(s => (
                  <WrapItem key={s.key}>
                    <Button size="xs" variant={filterStatusOut===s.key?'solid':'outline'}
                      colorScheme={filterStatusOut===s.key?s.color||'gray':'gray'}
                      onClick={() => { setFilterStatusOut(filterStatusOut===s.key?'':s.key); setPage(1); }}>
                      {s.label}
                    </Button>
                  </WrapItem>
                ))}
              </Wrap>
            </TabPanel>
            {/* £ OUT status filter */}
            <TabPanel px={0} pb={1} pt={2}>
              <Text fontSize="10px" color="gray.500" mb={1}>Filter by status:</Text>
              <Wrap spacing={1}>
                <WrapItem>
                  <Button size="xs" variant={filterStatusIn===''?'solid':'outline'} colorScheme={filterStatusIn===''?'red':'gray'}
                    onClick={() => { setFilterStatusIn(''); setPage(1); }}>All £ OUT</Button>
                </WrapItem>
                {inStatuses.filter(s=>s.enabled).map(s => (
                  <WrapItem key={s.key}>
                    <Button size="xs" variant={filterStatusIn===s.key?'solid':'outline'}
                      colorScheme={filterStatusIn===s.key?s.color||'gray':'gray'}
                      onClick={() => { setFilterStatusIn(filterStatusIn===s.key?'':s.key); setPage(1); }}>
                      {s.label}
                    </Button>
                  </WrapItem>
                ))}
              </Wrap>
            </TabPanel>
          </TabPanels>
        </Tabs>

        <HStack spacing={2} flexWrap="wrap" mb={2}>
          <FormControl w="130px"><FormLabel fontSize="xs" mb={0.5}>From</FormLabel>
            <Input size="sm" type="date" value={filterFrom} onChange={e => { setFilterFrom(e.target.value); setPage(1); }} /></FormControl>
          <FormControl w="130px"><FormLabel fontSize="xs" mb={0.5}>To</FormLabel>
            <Input size="sm" type="date" value={filterTo} onChange={e => { setFilterTo(e.target.value); setPage(1); }} /></FormControl>
          <Box pt={5}><Text fontSize="11px" color="gray.500">{total} invoices</Text></Box>
        </HStack>
      </Box>

      <Table size="sm"><Thead><Tr>
        <Th px={2}>Date</Th><Th px={2}>Ref</Th><Th px={2}>Project</Th><Th px={2}>Company</Th>
        <Th px={2}>Dir</Th><Th px={2} isNumeric>Net</Th><Th px={2} isNumeric>VAT</Th><Th px={2} isNumeric>Gross</Th>
        <Th px={2}>Currency</Th><Th px={2} isNumeric>Paid</Th><Th px={2}>Copy/New</Th><Th px={2}>Status</Th>
      </Tr></Thead><Tbody>
        {data.map(inv => (
          <Tr key={inv.id} _hover={{ bg:'blue.50' }} cursor="pointer" onClick={() => viewInvoice(inv.id)}>
            <Td px={2} whiteSpace="nowrap">{fmtDate(inv.issue_date)}</Td>
            <Td px={2} fontWeight="600" maxW="120px" isTruncated>{inv.invoice_ref || inv.invoice_number || '—'}</Td>
            <Td px={2} maxW="120px" isTruncated>{inv.project_name || '—'}</Td>
            <Td px={2} maxW="120px" isTruncated>{inv.company_name || inv.supplier_name || '—'}</Td>
            <Td px={2}><Badge colorScheme={inv.direction === 'out' ? 'green' : 'red'} fontSize="9px">
              {inv.direction === 'out' ? '£ IN' : '£ OUT'}</Badge></Td>
            <Td px={2} isNumeric>{fmtMoney(inv.total_net)}</Td>
            <Td px={2} isNumeric>{fmtMoney(inv.total_vat)}</Td>
            <Td px={2} isNumeric fontWeight="700">{fmtMoney(inv.total_gross)}</Td>
            <Td px={2}>
              {inv.currency_code && inv.currency_code !== 'GBP'
                ? <Badge colorScheme="blue" fontSize="8px">{inv.currency_code}</Badge>
                : <Text fontSize="10px" color="gray.400">GBP</Text>}
            </Td>
            <Td px={2} isNumeric color={parseFloat(inv.amount_paid) > 0 ? 'green.600' : 'gray.400'}>{fmtMoney(inv.amount_paid)}</Td>
            <Td px={2} onClick={e => e.stopPropagation()}>
              <HStack spacing={1}>
              <Menu placement="bottom-start" isLazy>
                <MenuButton as={Button} size="xs" fontSize="9px" variant="outline" colorScheme="blue"
                  leftIcon={<FiCopy size={11} />} rightIcon={<FiChevronDown size={11} />} px={2}>
                  Copy/New
                </MenuButton>
                <MenuList fontSize="12px" minW="280px" zIndex={1500}>
                  <MenuItem icon={<FiCopy size={13} />}
                    onClick={() => setNewInvoice({ projectId: inv.project_id, taskId: inv.task_id, direction: inv.direction || 'out', launchAction: { type: 'clone', invoiceId: inv.id } })}>
                    Copy invoice for this client as new
                  </MenuItem>
                  <MenuItem icon={<FiCopy size={13} />}
                    onClick={() => setNewInvoice({ projectId: inv.project_id, taskId: inv.task_id, direction: inv.direction || 'out', launchAction: { type: 'clone_other_client', invoiceId: inv.id } })}>
                    Copy the details of this invoice for a different client
                  </MenuItem>
                  <MenuItem icon={<FiEdit2 size={13} />}
                    onClick={() => setNewInvoice({ projectId: inv.project_id, taskId: inv.task_id, direction: inv.direction || 'out', launchAction: { type: 'blank' } })}>
                    Raise a new blank invoice for this client
                  </MenuItem>
                </MenuList>
              </Menu>
              {/* Assign money received — raised invoices awaiting payment only */}
              {inv.direction === 'out' && ASSIGNABLE_STATUSES.includes(inv.status) && (
                <Button size="xs" fontSize="9px" colorScheme="teal" variant="outline" px={2}
                  onClick={() => setAssignFocus(inv.id)}>
                  Assign £ Received
                </Button>
              )}
              </HStack>
            </Td>
            <Td px={2}>
              {!!inv.is_overdue && inv.status === 'paid_in_part' && (
                <Badge colorScheme="red" fontSize="7px" mb={0.5} display="block" w="fit-content">OVERDUE</Badge>
              )}
              <Select size="xs" fontSize="9px" w="110px" value={inv.status}
                bg={`${(inv.direction==='out'?outMap:inMap)[inv.status]?.color||STATUS_COLORS[inv.status]||'gray'}.50`}
                onClick={e => e.stopPropagation()}
                onChange={async (e) => {
                  e.stopPropagation();
                  try { await api.put(`/invoices/${inv.id}`, { status: e.target.value }); fetchData(); } catch(err) {}
                }}>
                {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </Select>
            </Td>
          </Tr>
        ))}
      </Tbody></Table>

      {totalPages > 1 && (
        <Flex justify="center" mt={3} gap={1}>
          <IconButton icon={<FiChevronLeft />} size="xs" isDisabled={page <= 1} onClick={() => setPage(p => p - 1)} aria-label="Prev" />
          <Text fontSize="11px" px={2} alignSelf="center">Page {page} of {totalPages}</Text>
          <IconButton icon={<FiChevronRight />} size="xs" isDisabled={page >= totalPages} onClick={() => setPage(p => p + 1)} aria-label="Next" />
        </Flex>
      )}

      {/* Copy/New launcher — opens the standard invoice form pre-populated, editable */}
      {newInvoice && (
        <InvoiceModal
          isOpen={true}
          projectId={newInvoice.projectId}
          taskId={newInvoice.taskId}
          direction={newInvoice.direction}
          launchAction={newInvoice.launchAction}
          onClose={() => { setNewInvoice(null); fetchData(); }}
        />
      )}
      {/* Assign money received — pre-focused on the row's invoice */}
      <AssignReceivedMoneyModal
        isOpen={!!assignFocus}
        focusInvoiceId={assignFocus}
        onClose={() => { setAssignFocus(null); fetchData(); }}
        onAssigned={fetchData}
      />
    </>
  );

  return (
    <PageShell fullPage={fullPage} onBack={onBack} isOpen={isOpen} onClose={onClose} title="Bookkeeper">
      {listContent}
    </PageShell>
  );
}
